import React, { useState, useRef } from 'react';
import { Mail, Phone, MapPin, Send, Instagram, Linkedin, Twitter, ArrowRight, X, CheckCircle2, Globe, Loader2 } from 'lucide-react';

const ContactPage: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // State for Modal Form
  const [modalFormStatus, setModalFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const modalFormRef = useRef<HTMLFormElement>(null);

  // State for Main Page Form
  const [mainFormStatus, setMainFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const mainFormRef = useRef<HTMLFormElement>(null);

  const handleGetStarted = () => {
    setIsModalOpen(true);
  };

  const handleTalkToSales = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /**
   * Cloudflare Function Integration Handler
   */
  const sendToBackend = async (
    data: any, 
    setStatus: React.Dispatch<React.SetStateAction<'idle' | 'submitting' | 'success' | 'error'>>,
    onSuccess?: () => void
  ) => {
    setStatus('submitting');

    try {
        // CORRECTION: In Cloudflare Pages, 'functions/send-email.js' maps to the URL '/send-email'
        // We removed the '/functions' prefix to ensure it hits the correct endpoint.
        const response = await fetch('/send-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            // Send the raw data object. The backend now constructs the HTML email.
            body: JSON.stringify(data)
        });

        if (!response.ok) {
            let errorMessage = 'Failed to send email';
            try {
                const resJson = await response.json();
                if (resJson.error) errorMessage = resJson.error;
            } catch (e) {
                // ignore parsing error
            }
            throw new Error(errorMessage);
        }
        
        setStatus('success');
        if (onSuccess) onSuccess();

    } catch (error) {
        console.error("Email send failed:", error);
        setStatus('error');
    }
  };

  const handleModalSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!modalFormRef.current) return;

    const formData = new FormData(modalFormRef.current);
    const payload = {
        name: formData.get('user_name'),
        email: formData.get('user_email'),
        phone: formData.get('user_phone'),
        location: formData.get('user_location'),
        type: 'Fast Track Inquiry',
        message: 'User requested a quote via the modal popup.',
        subject: 'Fast Track Quote Request'
    };

    sendToBackend(payload, setModalFormStatus, () => modalFormRef.current?.reset());
  };

  const handleMainFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!mainFormRef.current) return;

    const formData = new FormData(mainFormRef.current);
    const payload = {
        name: `${formData.get('first_name')} ${formData.get('last_name')}`,
        email: formData.get('email'),
        phone: formData.get('phone'),
        location: formData.get('location'),
        type: formData.get('requirement_type'),
        message: formData.get('message'),
        subject: `Contact Form: ${formData.get('requirement_type')}`
    };

    sendToBackend(payload, setMainFormStatus, () => mainFormRef.current?.reset());
  };

  return (
    <div className="-mt-20 pt-32 pb-24 min-h-screen relative overflow-hidden bg-gradient-to-b from-orange-50/60 via-white to-white">
      
      {/* Abstract Background Elements */}
      <div className="absolute top-0 right-0 -mr-20 -mt-20 w-[500px] h-[500px] bg-orange-200/40 rounded-full blur-[100px] opacity-60 z-0 pointer-events-none mix-blend-multiply animate-pulse-slow"></div>
      <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-[400px] h-[400px] bg-orange-100/60 rounded-full blur-[100px] opacity-70 z-0 pointer-events-none mix-blend-multiply"></div>
      <div className="absolute top-1/3 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-gradient-to-r from-orange-50/0 via-orange-100/20 to-orange-50/0 blur-[80px] z-0 pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-white/80 backdrop-blur-sm border border-orange-100 mb-6 shadow-sm">
            <span className="text-orange-600 text-xs font-bold uppercase tracking-widest">
                Start Your Project
            </span>
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 mb-6 tracking-tight">
            Contact BuildMySupplement
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Ready to start your custom supplement manufacturing project? We support clients across India and globally with formulation, manufacturing, and logistics.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-start mb-24">
          {/* Contact Info Side */}
          <div className="space-y-10">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h3>
              <p className="text-gray-600 leading-relaxed mb-8">
                Fill out the form and our team will get back to you as soon as possible.
                We serve clients in India and globally.
              </p>
              
              <div className="space-y-8">
                {/* Email */}
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mr-4 border border-gray-100 shadow-sm flex-shrink-0">
                    <Mail className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-1">Email Us</p>
                    <a href="mailto:team@buildmysupplement.com" className="text-lg font-bold text-gray-900 hover:text-orange-600 transition-colors break-all">
                      team@buildmysupplement.com
                    </a>
                  </div>
                </div>

                {/* India Location */}
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mr-4 border border-gray-100 shadow-sm flex-shrink-0">
                    <MapPin className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-1">India Office</p>
                    <p className="text-lg font-bold text-gray-900">
                      Sonipat, Haryana 13001,  India
                    </p>
                    <a href="tel:+919810042051" className="text-base text-gray-600 hover:text-orange-600 font-medium mt-1 block">
                      Phone: +91 98100 42051
                    </a>
                  </div>
                </div>

                {/* Global Location */}
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mr-4 border border-gray-100 shadow-sm flex-shrink-0">
                    <Globe className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-1">Global Office</p>
                    <p className="text-lg font-bold text-gray-900">
                      Hillsborough, Auckland 1042, New Zealand
                    </p>
                    <a href="tel:+642040444555" className="text-base text-gray-600 hover:text-orange-600 font-medium mt-1 block">
                      Phone: +64 20 4044 4555
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-10 border-t border-gray-100">
                <h4 className="text-lg font-bold text-gray-900 mb-4">Follow Us</h4>
                <div className="flex space-x-4">
                    <a href="#" aria-label="Instagram" className="w-10 h-10 bg-black text-white rounded-full flex items-center justify-center hover:bg-orange-500 hover:text-black transition-all">
                        <Instagram size={18} />
                    </a>
                    <a href="#" aria-label="LinkedIn" className="w-10 h-10 bg-black text-white rounded-full flex items-center justify-center hover:bg-orange-500 hover:text-black transition-all">
                        <Linkedin size={18} />
                    </a>
                    <a href="#" aria-label="Twitter" className="w-10 h-10 bg-black text-white rounded-full flex items-center justify-center hover:bg-orange-500 hover:text-black transition-all">
                        <Twitter size={18} />
                    </a>
                </div>
            </div>
          </div>

          {/* Main Form Side */}
          <div className="bg-white/60 backdrop-blur-md p-8 md:p-10 rounded-3xl border border-white shadow-xl relative ring-1 ring-gray-100">
            {mainFormStatus === 'success' ? (
                <div className="text-center py-16">
                    <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle2 size={40} />
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">Message Sent!</h3>
                    <p className="text-gray-600 mb-8 text-lg">Thank you. Our team will review your requirements and get back to you shortly.</p>
                    <button 
                        onClick={() => setMainFormStatus('idle')}
                        className="px-8 py-3 bg-black text-white rounded-xl font-bold hover:bg-gray-800 transition-colors"
                    >
                        Send Another
                    </button>
                </div>
            ) : (
                <form ref={mainFormRef} className="space-y-6" onSubmit={handleMainFormSubmit}>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-900">First Name <span className="text-red-500">*</span></label>
                      <input 
                        type="text" 
                        name="first_name"
                        required
                        placeholder="John" 
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 bg-white/80 transition-all"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-900">Last Name <span className="text-red-500">*</span></label>
                      <input 
                        type="text" 
                        name="last_name"
                        required
                        placeholder="Doe" 
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 bg-white/80 transition-all"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-900">Business Email <span className="text-red-500">*</span></label>
                      <input 
                        type="email" 
                        name="email"
                        required
                        placeholder="john@company.com" 
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 bg-white/80 transition-all"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-gray-900">Phone Number <span className="text-red-500">*</span></label>
                      <input 
                        type="tel" 
                        name="phone"
                        required
                        placeholder="+91 98765 43210" 
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 bg-white/80 transition-all"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-bold text-gray-900">Country / Region <span className="text-red-500">*</span></label>
                    <input 
                      type="text" 
                      name="location"
                      required
                      placeholder="e.g. India, USA, UK" 
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 bg-white/80 transition-all"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-bold text-gray-900">Requirement Type <span className="text-red-500">*</span></label>
                    <select 
                        name="requirement_type"
                        required
                        defaultValue=""
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 bg-white/80 transition-all"
                    >
                        <option value="" disabled>Select Requirement Type</option>
                        <option value="New Brand / Startup D2C Founder">New Brand / Startup D2C Founder</option>
                        <option value="Contract Manufacturing (Domestic)">Contract Manufacturing (Domestic)</option>
                        <option value="International Export / Private Label">International Export / Private Label</option>
                        <option value="Bulk Ingredient Sourcing">Bulk Ingredient Sourcing</option>
                        <option value="Custom Formulation">Custom Formulation</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-bold text-gray-900">Message <span className="text-red-500">*</span></label>
                    <textarea 
                      name="message"
                      required
                      rows={4}
                      placeholder="Tell us about your volume requirements and target market..." 
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 bg-white/80 transition-all resize-none"
                    />
                  </div>

                  <button 
                    type="submit"
                    disabled={mainFormStatus === 'submitting'}
                    className="w-full py-4 bg-black text-white font-bold rounded-xl hover:bg-orange-500 hover:text-black transition-all shadow-lg hover:shadow-orange-500/25 flex items-center justify-center text-lg transform hover:-translate-y-1 disabled:opacity-70 disabled:hover:translate-y-0"
                  >
                    {mainFormStatus === 'submitting' ? (
                        <>Sending <Loader2 className="w-5 h-5 ml-2 animate-spin" /></>
                    ) : (
                        <>Send Inquiry <Send className="w-5 h-5 ml-2" /></>
                    )}
                  </button>
                  {mainFormStatus === 'error' && (
                      <p className="text-red-500 text-center text-sm font-bold">Failed to send message. Please try again.</p>
                  )}
                </form>
            )}
          </div>
        </div>

        {/* Modern Large CTA Section - Dark Theme */}
        <div className="mt-12 relative rounded-[3rem] border border-gray-800 bg-black shadow-2xl overflow-hidden">
             {/* Background Gradients */}
            <div className="absolute top-0 left-0 w-full h-full z-0 pointer-events-none">
                 <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[80%] bg-gradient-to-br from-orange-600/20 to-transparent rounded-full blur-[80px]"></div>
                 <div className="absolute bottom-[-20%] right-[-10%] w-[60%] h-[80%] bg-gradient-to-tl from-gray-600/20 to-transparent rounded-full blur-[80px]"></div>
                 {/* Faint noise overlay */}
                 <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.5'/%3E%3C/svg%3E")` }}></div>
            </div>

            <div className="relative z-10 px-8 py-20 md:py-24 text-center">
                 <h2 className="text-4xl md:text-5xl font-extrabold text-white tracking-tight mb-6 leading-tight">
                    World-class manufacturing.<br className="hidden md:block" /> For Indian & Global Brands.
                 </h2>
                 <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed font-medium">
                    Partner with India's leading supplement manufacturer.
                 </p>
                 
                 <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <button 
                        onClick={handleGetStarted}
                        className="px-10 py-4 rounded-full bg-orange-600 text-white font-bold text-lg hover:bg-orange-700 transition-all shadow-xl hover:shadow-orange-500/20 flex items-center w-full sm:w-auto justify-center group transform hover:-translate-y-1"
                    >
                        Request Quote <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </button>
                    <button 
                        onClick={handleTalkToSales}
                        className="px-10 py-4 rounded-full bg-transparent text-white border border-gray-700 font-bold text-lg hover:bg-gray-900 hover:border-gray-600 transition-all shadow-sm w-full sm:w-auto"
                    >
                        Talk to Sales
                    </button>
                 </div>
            </div>
        </div>

      </div>

      {/* Lead Capture Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setIsModalOpen(false)}></div>
          
          <div className="bg-white rounded-3xl p-8 max-w-md w-full relative z-10 shadow-2xl animate-in fade-in zoom-in duration-300">
            <button 
                onClick={() => setIsModalOpen(false)}
                className="absolute top-4 right-4 p-2 bg-gray-100 rounded-full text-gray-500 hover:text-black hover:bg-gray-200 transition-colors"
            >
                <X size={20} />
            </button>

            {modalFormStatus === 'success' ? (
                <div className="text-center py-12">
                    <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle2 size={32} />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Inquiry Received!</h3>
                    <p className="text-gray-600 mb-6">Our team will review your requirements and contact you shortly.</p>
                    <button 
                        onClick={() => setIsModalOpen(false)}
                        className="px-6 py-2 bg-black text-white rounded-lg font-bold"
                    >
                        Close
                    </button>
                </div>
            ) : modalFormStatus === 'error' ? (
                <div className="text-center py-12">
                    <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                        <X size={32} />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Something went wrong</h3>
                    <p className="text-gray-600 mb-6">We couldn't send your message. Please try again later.</p>
                    <button 
                        onClick={() => setModalFormStatus('idle')}
                        className="px-6 py-2 bg-black text-white rounded-lg font-bold"
                    >
                        Try Again
                    </button>
                </div>
            ) : (
                <>
                    <div className="text-center mb-6">
                        <div className="inline-flex items-center px-3 py-1 rounded-full bg-orange-50 text-orange-600 text-xs font-bold uppercase tracking-wide mb-3">
                            Fast Track Inquiry
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900">Start Your Project</h3>
                        <p className="text-gray-500 mt-2 text-sm">We specialize in contract manufacturing.</p>
                    </div>

                    <form ref={modalFormRef} onSubmit={handleModalSubmit} className="space-y-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Full Name</label>
                            <input type="text" name="user_name" required className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 bg-gray-50/50" placeholder="Your Name" />
                        </div>
                        
                        <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Email Address</label>
                            <input type="email" name="user_email" required className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 bg-gray-50/50" placeholder="you@company.com" />
                        </div>
                        
                        <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Phone Number</label>
                            <input type="tel" name="user_phone" required className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 bg-gray-50/50" placeholder="+91 98765 43210" />
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Location</label>
                             <input type="text" name="user_location" required className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 bg-gray-50/50" placeholder="e.g. Mumbai, New York" />
                        </div>

                         <input type="hidden" name="message" value="New inquiry from Fast Track form" />

                        <button 
                            type="submit" 
                            disabled={modalFormStatus === 'submitting'}
                            className="w-full py-3.5 rounded-xl bg-orange-600 text-white font-bold text-lg hover:bg-orange-700 transition-all shadow-lg hover:shadow-orange-500/20 flex items-center justify-center mt-2"
                        >
                            {modalFormStatus === 'submitting' ? 'Submitting...' : 'Request Quote'}
                            {!modalFormStatus && <ArrowRight className="ml-2 w-5 h-5" />}
                        </button>
                    </form>
                    <p className="text-xs text-center text-gray-400 mt-4">100% confidential. No commitment required.</p>
                </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactPage;